import * as THREE from 'three';
import { CHARGE_SPARKS as FX } from '../config/chargeSparks';
import { mulberry32 } from '../utils/prng';
import type { Vec3 } from '../utils/math';
import type { Box } from '../world/Level';

interface Spark {
  origin: THREE.Vector3;
  velocity: THREE.Vector3;
  delay: number;
  lifetime: number;
}

/** A finite burst sampled by age: pausing, low frame rates and restarting are safe. */
export class ChargeSparks {
  readonly root = new THREE.Group();
  private readonly geometry = new THREE.CylinderGeometry(1, 1, 1, 5);
  private readonly sparkMaterial = new THREE.MeshBasicMaterial({
    color: 0xffffff, transparent: true, blending: THREE.AdditiveBlending,
    depthWrite: false, toneMapped: false,
  });
  private readonly arcMaterial = new THREE.MeshBasicMaterial({
    color: 0x80dfff, transparent: true, blending: THREE.AdditiveBlending,
    depthWrite: false, toneMapped: false,
  });
  private readonly streaks = new THREE.InstancedMesh(this.geometry, this.sparkMaterial, FX.count);
  private readonly arcs = new THREE.InstancedMesh(this.geometry, this.arcMaterial, 4 * FX.arcSegments);
  private readonly light = new THREE.PointLight(0x82dfff, 0, FX.lightDistance, 2);
  private readonly glow: THREE.Sprite;
  private readonly sparks: Spark[] = [];
  private readonly paths: THREE.Vector3[][] = [];
  private readonly pose = new THREE.Object3D();
  private readonly start = new THREE.Vector3();
  private readonly end = new THREE.Vector3();
  private readonly direction = new THREE.Vector3();
  private readonly up = new THREE.Vector3(0, 1, 0);

  constructor(port: Vec3, car: Box) {
    this.root.name = 'winkzi-charge-sparks';
    this.root.position.set(port.x, port.y, port.z);
    this.root.visible = false;
    // Bounds change with every burst. These small batches must not be culled
    // using the empty instance matrices they had before the first cable hit.
    for (const mesh of [this.streaks, this.arcs]) {
      mesh.frustumCulled = false;
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    }
    const rng = mulberry32(FX.seed);
    const roof = car.maxY - port.y + 0.12;
    const front = car.minX - port.x + 0.5;
    const back = car.maxX - port.x - 0.5;
    const near = car.minZ - port.z - 0.06;
    const far = car.maxZ - port.z + 0.06;
    const color = new THREE.Color();
    for (let i = 0; i < FX.count; i++) {
      const angle = rng() * Math.PI * 2;
      const speed = FX.speed[0] + rng() * (FX.speed[1] - FX.speed[0]);
      const fromPort = i % 3 !== 0;
      this.sparks.push({
        origin: fromPort ? new THREE.Vector3() : new THREE.Vector3(
          front + rng() * (back - front), roof, i % 2 ? near : far,
        ),
        velocity: new THREE.Vector3(
          Math.cos(angle) * speed * 0.6,
          speed * (0.65 + rng() * 0.35),
          fromPort ? -Math.abs(Math.sin(angle)) * speed * 0.7 : Math.sin(angle) * speed * 0.5,
        ),
        delay: i < 48 ? 0 : rng() * FX.emissionDuration,
        lifetime: FX.lifetime[0] + rng() * (FX.lifetime[1] - FX.lifetime[0]),
      });
      this.streaks.setColorAt(i, color.setHex(i % 4 === 0 ? 0xa3eaff : 0xffbd58));
    }
    for (const x of [front, back]) {
      for (const z of [near, far]) {
        this.paths.push([
          new THREE.Vector3(), new THREE.Vector3(x, roof * 0.55, near),
          new THREE.Vector3(x * 0.7, roof, near), new THREE.Vector3(x * 0.7, roof, z),
        ]);
      }
    }
    const pixels = new Uint8Array(32 * 32 * 4);
    for (let y = 0; y < 32; y++) {
      for (let x = 0; x < 32; x++) {
        const offset = (y * 32 + x) * 4;
        const falloff = Math.max(0, 1 - Math.hypot(x - 15.5, y - 15.5) / 16);
        pixels.set([255, 255, 255, Math.round(255 * falloff ** 3)], offset);
      }
    }
    const texture = new THREE.DataTexture(pixels, 32, 32);
    texture.needsUpdate = true;
    this.glow = new THREE.Sprite(new THREE.SpriteMaterial({
      map: texture, color: 0xa3eaff, blending: THREE.AdditiveBlending,
      depthWrite: false, toneMapped: false,
    }));
    this.glow.position.z = -0.1;
    this.light.position.set(0, 0.5, -0.4);
    this.root.add(this.streaks, this.arcs, this.glow, this.light);
  }

  render(age: number | null): void {
    this.root.visible = age !== null && age >= 0 && age < FX.duration;
    if (!this.root.visible || age === null) {
      this.light.intensity = 0;
      return;
    }
    for (let i = 0; i < this.sparks.length; i++) {
      const spark = this.sparks[i];
      const t = age - spark.delay;
      const alive = t >= 0 && t < spark.lifetime;
      this.sample(spark, Math.max(0, t), this.end);
      this.sample(spark, Math.max(0, t - FX.trailSeconds), this.start);
      const fade = Math.max(0, 1 - t / spark.lifetime);
      this.segment(this.streaks, i, alive ? FX.thickness * Math.sqrt(fade) : 0);
    }
    this.streaks.instanceMatrix.needsUpdate = true;
    const energy = Math.max(0, 1 - age / FX.arcDuration);
    this.arcs.visible = energy > 0;
    if (this.arcs.visible) {
      const noise = mulberry32(FX.seed + Math.floor(age * FX.arcRefreshHz));
      for (let pathIndex = 0; pathIndex < this.paths.length; pathIndex++) {
        const path = this.paths[pathIndex];
        this.start.copy(path[0]);
        for (let i = 1; i <= FX.arcSegments; i++) {
          const progress = (i / FX.arcSegments) * (path.length - 1);
          const section = Math.min(path.length - 2, Math.floor(progress));
          this.end.lerpVectors(path[section], path[section + 1], progress - section);
          if (i < FX.arcSegments) {
            this.end.x += (noise() - 0.5) * FX.arcJitter;
            this.end.y += (noise() - 0.5) * FX.arcJitter;
            this.end.z += (noise() - 0.5) * FX.arcJitter;
          }
          this.segment(this.arcs, pathIndex * FX.arcSegments + i - 1, FX.arcThickness * energy);
          this.start.copy(this.end);
        }
      }
      this.arcs.instanceMatrix.needsUpdate = true;
    }
    this.arcMaterial.opacity = energy;
    this.glow.material.opacity = energy * energy;
    this.glow.scale.setScalar(FX.glowSize * (0.5 + energy));
    this.light.intensity = FX.lightIntensity * energy ** 2 * (0.85 + 0.15 * Math.cos(age * 18));
  }

  private sample(spark: Spark, age: number, out: THREE.Vector3): void {
    out.copy(spark.origin).addScaledVector(spark.velocity, age);
    out.y = Math.max(0.025 - this.root.position.y, out.y - 0.5 * FX.gravity * age * age);
  }

  private segment(mesh: THREE.InstancedMesh, index: number, radius: number): void {
    this.direction.subVectors(this.end, this.start);
    const length = this.direction.length();
    this.pose.position.copy(this.start).add(this.end).multiplyScalar(0.5);
    if (length > 0) this.pose.quaternion.setFromUnitVectors(this.up, this.direction.divideScalar(length));
    this.pose.scale.set(radius, length, radius);
    this.pose.updateMatrix();
    mesh.setMatrixAt(index, this.pose.matrix);
  }

  dispose(): void {
    this.geometry.dispose();
    this.sparkMaterial.dispose();
    this.arcMaterial.dispose();
    this.streaks.dispose();
    this.arcs.dispose();
    this.glow.material.map?.dispose();
    this.glow.material.dispose();
    this.root.removeFromParent();
  }
}
